<script lang="ts">
  // Docked timeline (video-player style), driven by the active dataset's timeline.json and the shared player in
  // app state. Live: replay tonight so far, "● LIVE" / "Go live". Replay datasets (amber): starts at 7 PM,
  // "End of night" + "Replay again" at the last snapshot. Every position loads that snapshot's immutable files.
  import { app } from '../../lib/app.svelte';
  import { fmtTime } from '../../lib/format';
  import { SPEEDS, timeTicks, type Speed } from '../../lib/timeline';

  const pts = $derived(app.points);
  const ts = $derived(app.times);
  const t0 = $derived(ts[0] ?? 0);
  const t1 = $derived(ts[ts.length - 1] ?? 0);
  const idx = $derived(app.viewIndex);
  const ticks = $derived(timeTicks(t0, t1, 7));
  const pos = (t: number) => (t1 > t0 ? Math.max(0, Math.min(100, ((t - t0) / (t1 - t0)) * 100)) : 100);
  const shown = $derived(app.displayTime ?? t1);
  const replay = $derived(!!app.replay);
  // only on replay datasets: on the live night the bar read as the 2024 replay leaking into live results
  const show = $derived(pts.length > 1 && (replay || app.final));
  const hm = (t: number) => fmtTime(t).replace(' ET', '');

  // The page reserves exactly the docked bar's real height (incl. the iOS home-indicator inset) at the bottom,
  // so cards never slide under it; 0 when there is no timeline to replay.
  let barH = $state(0);
  $effect(() => {
    document.documentElement.style.setProperty('--scrubber-h', `${show ? barH : 0}px`);
  });
  function onInput(e: Event) {
    app.pause();
    app.seek(Number((e.target as HTMLInputElement).value));
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { app.step(-1); e.preventDefault(); }
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { app.step(1); e.preventDefault(); }
    else if (e.key === 'Home') { app.pause(); app.seek(t0); e.preventDefault(); }
    else if (e.key === 'End') { app.toEnd(); e.preventDefault(); }
    else if (e.key === ' ' || e.key === 'k') { if (app.playing) app.pause(); else app.play(); e.preventDefault(); }
  }
</script>

{#if show}
  <div class="scrub" class:replay role="region" aria-label={replay ? `Replay of the ${app.meta?.dataset?.label ?? 'election night'}` : app.final ? 'Replay election night' : 'Replay tonight so far'} bind:offsetHeight={barH}>
    <div class="inner">
      <div class="btns">
        <button class="step" type="button" onclick={() => app.step(-1)} aria-label="Previous update" disabled={idx <= 0 && (app.clock === null || app.clock <= t0)}>
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5v14M19 5.5v13a1 1 0 0 1-1.5.86L8 13a1.2 1.2 0 0 1 0-2l9.5-6.36A1 1 0 0 1 19 5.5z" fill="currentColor" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" /></svg>
        </button>
        {#if replay && app.atEnd && !app.playing}
          <button class="play again" type="button" onclick={() => app.play()} aria-label="Replay the night again">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 1 0 2.6-5.9M4 4v5h5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>
        {:else}
          <button class="play" type="button" onclick={() => (app.playing ? app.pause() : app.play())} aria-label={app.playing ? 'Pause' : replay ? 'Play election night' : 'Replay the night'}>
            {#if app.playing}<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1.5" fill="currentColor" /><rect x="14" y="5" width="4" height="14" rx="1.5" fill="currentColor" /></svg>
            {:else}<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="currentColor" /></svg>{/if}
          </button>
        {/if}
        <button class="step" type="button" onclick={() => app.step(1)} aria-label="Next update" disabled={idx >= pts.length - 1}>
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M18 5v14M5 5.5v13a1 1 0 0 0 1.5.86L16 13a1.2 1.2 0 0 0 0-2L6.5 4.64A1 1 0 0 0 5 5.5z" fill="currentColor" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" /></svg>
        </button>
      </div>
      <div class="when">
        {#if replay}
          <span class="badge rp" class:mid={!(app.atEnd && !app.playing)}>{app.atEnd && !app.playing ? 'End of night' : `Replay ${app.meta?.dataset?.year ?? ''}`.trim()}</span>
        {:else if app.isLive}
          <span class="badge live" title="You are seeing the latest results">{app.final ? 'Final' : '● LIVE'}</span>
        {:else}
          <button class="badge go" type="button" onclick={() => app.toEnd()} title="Jump to the latest results">{app.final ? 'Latest' : 'Go live'}</button>
        {/if}
        <span class="time num" aria-live="off">{hm(shown)}</span>
      </div>
      <div class="track-wrap">
        <div class="track" aria-hidden="true">
          <div class="fill" style="width:{pos(shown)}%"></div>
          {#if ts.length <= 60}{#each ts as t, i}<i class="snap" class:past={i <= idx} style="left:{pos(t)}%"></i>{/each}{/if}
          <span class="knob" style="left:{pos(shown)}%"></span>
        </div>
        <input class="range" type="range" min={t0} max={t1} step="1000" value={shown} oninput={onInput} onkeydown={onKey}
          aria-label={replay ? 'Election night timeline' : 'Results timeline'} aria-valuetext="{fmtTime(shown)}, update {idx + 1} of {pts.length}{app.isLive ? ' (live)' : ''}" />
        <div class="ticks" aria-hidden="true">
          {#each ticks as tk}<span style="left:{pos(tk.t)}%" class:first={pos(tk.t) < 4} class:last={pos(tk.t) > 96}>{tk.label}</span>{/each}
        </div>
      </div>
      <div class="speed" role="group" aria-label="Replay speed (how much faster than real time)">
        {#each SPEEDS as s}
          <button type="button" aria-pressed={app.speed === s} onclick={() => (app.speed = s as Speed)} title="{s}× real time">{s}×</button>
        {/each}
      </div>
    </div>
  </div>
{/if}

<style>
  .scrub { --acc: var(--est); --acc-ink: var(--est-ink); position: fixed; left: 0; right: 0; bottom: 0; z-index: 55; padding: 0 12px calc(10px + env(safe-area-inset-bottom)); pointer-events: none; }
  .scrub.replay { --acc: var(--rp); --acc-ink: var(--rp-ink); }
  .inner { pointer-events: auto; max-width: 1240px; margin: 0 auto; display: flex; align-items: center; gap: 14px; background: var(--card); border: 1px solid var(--line); border-radius: 18px; box-shadow: var(--shadow-lg); padding: 10px 16px 10px 10px; }
  .replay .inner { border: 2px solid var(--rp); background: color-mix(in oklab, var(--rp) 6%, var(--card)); }
  .btns { display: flex; align-items: center; gap: 4px; flex: none; }
  .play { flex: none; width: 44px; height: 44px; border-radius: 50%; border: 0; background: var(--acc); color: #fff; display: grid; place-items: center; cursor: pointer; box-shadow: 0 4px 12px color-mix(in oklab, var(--acc) 45%, transparent); }
  .replay .play { color: #1a1205; }
  .play:hover { filter: brightness(1.08); }
  .step { width: 32px; height: 32px; border-radius: 50%; border: 0; background: transparent; color: var(--ink-2); display: grid; place-items: center; cursor: pointer; }
  .step:hover:not(:disabled) { background: var(--card-3); }
  .step:disabled { opacity: 0.35; cursor: default; }
  .when { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; min-width: 92px; }
  .badge { border: 0; font: 800 0.66rem/1 var(--font); letter-spacing: 0.08em; text-transform: uppercase; padding: 4px 7px; border-radius: 6px; white-space: nowrap; }
  .badge.live { background: color-mix(in oklab, var(--bad) 14%, transparent); color: var(--bad); }
  .badge.go { background: var(--card-3); color: var(--ink-3); cursor: pointer; }
  .badge.rp { background: var(--rp); color: #1a1205; }
  .time { font: 700 1.05rem/1.1 var(--display); }
  .track-wrap { position: relative; flex: 1; height: 40px; min-width: 0; }
  .track { position: absolute; left: 0; right: 0; top: 10px; height: 6px; border-radius: 999px; background: var(--card-3); }
  .fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 999px; background: var(--acc); }
  .snap { position: absolute; top: -3px; width: 2px; height: 12px; margin-left: -1px; background: var(--line-2); border-radius: 1px; }
  .snap.past { background: color-mix(in oklab, var(--acc) 55%, var(--card)); }
  .knob { position: absolute; top: 50%; width: 18px; height: 18px; margin: -9px 0 0 -9px; border-radius: 50%; background: var(--card); border: 3px solid var(--acc); box-shadow: var(--shadow); }
  .range { position: absolute; inset: 0 -9px 14px; width: calc(100% + 18px); opacity: 0; cursor: pointer; margin: 0; }
  .track-wrap:focus-within .knob { box-shadow: 0 0 0 4px color-mix(in oklab, var(--acc) 30%, transparent); }
  .ticks { position: absolute; left: 0; right: 0; top: 24px; height: 14px; }
  .ticks span { position: absolute; transform: translateX(-50%); font-size: 0.7rem; color: var(--ink-3); white-space: nowrap; font-variant-numeric: tabular-nums; }
  .ticks span.first { transform: none; }
  .ticks span.last { transform: translateX(-100%); }
  .speed { display: flex; background: var(--card-3); border-radius: 999px; padding: 3px; flex: none; }
  .speed button { border: 0; background: none; padding: 5px 8px; border-radius: 999px; font: 700 0.74rem/1 var(--font); color: var(--ink-3); cursor: pointer; }
  .speed button[aria-pressed='true'] { background: var(--card); color: var(--ink); box-shadow: 0 1px 3px rgba(16, 24, 40, 0.15); }
  @media (max-width: 640px) {
    .scrub { padding: 0 6px calc(6px + env(safe-area-inset-bottom)); }
    .inner { flex-wrap: wrap; gap: 2px 8px; padding: 6px 10px 2px 6px; border-radius: 16px; }
    .play { width: 38px; height: 38px; }
    .step { width: 28px; height: 28px; }
    .when { flex-direction: row; align-items: center; gap: 6px; min-width: 0; }
    .time { font-size: 0.98rem; }
    .speed { margin-left: auto; }
    .speed button { padding: 5px 5px; font-size: 0.68rem; }
    .badge.rp.mid { display: none; } /* the amber strip already says REPLAY */
    .btns { gap: 0; }
    .track-wrap { flex: 1 1 100%; order: 4; height: 36px; }
  }
</style>
